import { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SendIcon from '@mui/icons-material/Send';
import { marked } from 'marked';
import { useCopilotChat } from './useCopilotChat';
import { DiseaseDialog } from './DiseaseDialog';

marked.setOptions({ breaks: true, gfm: true });

// Case urns: hide entirely. Disease urns: turn into clickable span.
// Other urns (drug/symptom/procedure/source/document): strip to plain text.
const URN_ANCHOR_RE =
  /<a\s+[^>]*href=["']urn:gekkovet:([a-z]+):([^"']+)["'][^>]*>([\s\S]*?)<\/a>/gi;

const transformUrnLinks = (html: string): string =>
  html.replace(
    URN_ANCHOR_RE,
    (_match, entityType: string, entityId: string, label: string) => {
      if (entityType === 'case') return '';
      if (entityType === 'disease') {
        const idNum = parseInt(entityId, 10);
        if (!Number.isFinite(idNum) || idNum <= 0) return label;
        return `<span class="gv-disease-link" data-disease-id="${idNum}" role="button" tabindex="0">${label}</span>`;
      }
      return label;
    }
  );

const renderMarkdown = (text: string): string => {
  try {
    const out = marked.parse(text, { async: false });
    return transformUrnLinks(typeof out === 'string' ? out : text);
  } catch {
    return text;
  }
};

interface ChatViewProps {
  caseId: string;
  endpoint: string;
  onOpenDiagnostics: () => void;
}

export const ChatView = ({
  caseId,
  endpoint,
  onOpenDiagnostics,
}: ChatViewProps) => {
  const { messages, isSending, error, sendMessage } = useCopilotChat({
    endpoint,
    caseId,
  });
  const [input, setInput] = useState('');
  const [diseaseId, setDiseaseId] = useState<number | null>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const handleMessagesClick = (e: React.MouseEvent<HTMLDivElement>) => {
    const target = (e.target as HTMLElement).closest(
      '.gv-disease-link'
    ) as HTMLElement | null;
    if (!target) return;
    const idStr = target.getAttribute('data-disease-id');
    const id = idStr ? parseInt(idStr, 10) : NaN;
    if (Number.isFinite(id) && id > 0) {
      setDiseaseId(id);
    }
  };

  useEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [messages]);

  const handleSend = () => {
    if (!input.trim() || isSending) return;
    const text = input;
    setInput('');
    sendMessage(text);
  };

  return (
    <Box
      sx={{
        height: '100%',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          p: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6">Ask assistant</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<OpenInNewIcon />}
          onClick={onOpenDiagnostics}
        >
          View diagnostics
        </Button>
      </Box>

      <Box
        ref={listRef}
        onClick={handleMessagesClick}
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {messages.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Ask a question about this case. The assistant has access to the
            current case context.
          </Typography>
        )}
        {messages.map((m) => (
          <Box
            key={m.id}
            sx={{
              alignSelf: m.sender === 'user' ? 'flex-end' : 'flex-start',
              maxWidth: '85%',
              bgcolor:
                m.sender === 'user'
                  ? 'primary.main'
                  : m.isProgress
                  ? 'grey.100'
                  : 'grey.200',
              color:
                m.sender === 'user' ? 'primary.contrastText' : 'text.primary',
              px: 1.5,
              py: 1,
              borderRadius: 2,
              fontStyle: m.isProgress ? 'italic' : 'normal',
              whiteSpace: m.sender === 'user' ? 'pre-wrap' : 'normal',
              wordBreak: 'break-word',
              '& p': { m: 0 },
              '& p + p': { mt: 1 },
              '& ul, & ol': { pl: 2.5, my: 0.5 },
              '& li': { mb: 0.25 },
              '& code': {
                bgcolor: 'rgba(0,0,0,0.06)',
                px: 0.5,
                borderRadius: 0.5,
                fontSize: '0.85em',
              },
              '& pre': {
                bgcolor: 'rgba(0,0,0,0.06)',
                p: 1,
                borderRadius: 1,
                overflowX: 'auto',
              },
              '& a': { color: 'inherit', textDecoration: 'underline' },
              '& .gv-disease-link': {
                color: 'primary.main',
                textDecoration: 'underline',
                cursor: 'pointer',
                fontWeight: 500,
              },
            }}
          >
            {m.sender === 'user' || m.isProgress ? (
              <Typography variant="body2">{m.text}</Typography>
            ) : (
              <Box
                sx={{ fontSize: '0.875rem', lineHeight: 1.5 }}
                dangerouslySetInnerHTML={{ __html: renderMarkdown(m.text) }}
              />
            )}
          </Box>
        ))}
        {isSending &&
          !messages.some((m) => m.isProgress || m.id === 'streaming') && (
            <Box
              sx={{
                alignSelf: 'flex-start',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Thinking...
              </Typography>
            </Box>
          )}
        {error && (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          display: 'flex',
          gap: 1,
        }}
      >
        <TextField
          multiline
          maxRows={4}
          fullWidth
          size="small"
          placeholder="Ask about this case..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <IconButton
          color="primary"
          onClick={handleSend}
          disabled={!input.trim() || isSending}
          aria-label="Send message"
        >
          <SendIcon />
        </IconButton>
      </Box>
      <DiseaseDialog
        open={diseaseId !== null}
        diseaseId={diseaseId}
        onClose={() => setDiseaseId(null)}
      />
    </Box>
  );
};
